import { lazy, Suspense, Component, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider }        from '@tanstack/react-query'
import { AuthProvider }     from '@/modules/auth/AuthProvider'
import { ProtectedRoute }   from '@/modules/auth/ProtectedRoute'
import { LoginPage }        from '@/modules/auth/LoginPage'
import { AppShell }         from '@/layouts/AppShell'
import { Loader2 }          from 'lucide-react'
import { useAuthStore }     from '@/stores/auth.store'

const DashboardPage        = lazy(() => import('@/modules/dashboard/DashboardPage'))
const CampanasPage         = lazy(() => import('@/modules/campanas/CampanasPage'))
const MarcadoPage          = lazy(() => import('@/modules/campanas/MarcadoPage'))
const MarcadosPage         = lazy(() => import('@/modules/marcados/MarcadosPage'))
const CobrosPage           = lazy(() => import('@/modules/cobros/CobrosPage'))
const CobrosTrabajarRedirect = lazy(() => import('@/modules/cobros/CobrosTrabajarRedirect'))
const ImpugnacionesPage    = lazy(() => import('@/modules/impugnaciones/ImpugnacionesPage'))
const EmbargoPage          = lazy(() => import('@/modules/embargo/EmbargoPage'))
const HabeasPage           = lazy(() => import('@/modules/habeas/HabeasPage'))
const ClientesPage         = lazy(() => import('@/modules/clientes/ClientesPage'))
const ReportesPage         = lazy(() => import('@/modules/reportes/ReportesPage'))
const UsuariosPage         = lazy(() => import('@/modules/usuarios/UsuariosPage'))
const ConfiguracionPage    = lazy(() => import('@/modules/configuracion/ConfiguracionPage'))
const AgendaPage           = lazy(() => import('@/modules/agenda/AgendaPage'))
const GestionesLegalesPage = lazy(() => import('@/modules/gestiones-legales/GestionesLegalesPage'))
const EmbargosLegalPage    = lazy(() => import('@/modules/legal/EmbargosPage'))
const LevantamientosPage   = lazy(() => import('@/modules/legal/LevantamientosPage'))
const WorkspacePage        = lazy(() => import('@/modules/call-center/WorkspacePage'))
const SupervisionPage      = lazy(() => import('@/modules/call-center/SupervisionPage'))
const PagosPage            = lazy(() => import('@/modules/call-center/PagosPage'))
const MarcadorListasPage   = lazy(() => import('@/modules/call-center/MarcadorListasPage'))

function ModuleLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen text-[var(--text-muted)]">
      <Loader2 size={18} className="animate-spin" />
    </div>
  )
}

class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) {
    return { error }
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-6 bg-[#0d0d1a] text-white">
          <p className="text-sm font-semibold">Error en Call Center Workspace</p>
          <pre className="text-xs text-red-300 max-w-lg whitespace-pre-wrap">{this.state.error.message}</pre>
          <a href="/" className="text-xs text-blue-400 underline">Volver al CRM</a>
        </div>
      )
    }
    return this.props.children
  }
}

/** Pantalla mínima para validar que el router monta fuera de AppShell */
function CcSmoke() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-[#0d0d1a] text-white p-6">
      <p className="text-sm font-semibold">Call Center — ruta OK</p>
      <p className="text-xs text-white/60">Si ves esto, el router funciona. Usa /workspace con sesión.</p>
      <a className="text-xs text-blue-400 underline" href="/workspace">Ir a Workspace</a>
      <a className="text-xs text-blue-400 underline" href="/">CRM</a>
    </div>
  )
}

function LegalGuard({ children }: { children: React.ReactNode }) {
  const role = useAuthStore(s => s.role)
  if (role !== 'admin' && role !== 'agente_legal') {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime:            1000 * 60 * 2,
      gcTime:               1000 * 60 * 10,
      retry:                1,
      refetchOnWindowFocus: false,
    },
  },
})

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ErrorBoundary>
            <Suspense fallback={<ModuleLoader />}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />

                {/* Diagnóstico: sin auth, sin lazy */}
                <Route path="/cc-test" element={<CcSmoke />} />

                <Route path="/CC" element={<Navigate to="/workspace" replace />} />
                <Route path="/CC/" element={<Navigate to="/workspace" replace />} />
                <Route path="/cc" element={<Navigate to="/workspace" replace />} />
                <Route path="/cc/" element={<Navigate to="/workspace" replace />} />

                {/* Workspace real — path /workspace (evita conflicto raro con /cc) */}
                <Route element={<ProtectedRoute />}>
                  <Route
                    path="/workspace"
                    element={
                      <ErrorBoundary>
                        <WorkspacePage />
                      </ErrorBoundary>
                    }
                  />
                </Route>

                <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
                  <Route path="/"                  element={<DashboardPage />} />
                  <Route path="/marcados"          element={<MarcadosPage />} />
                  <Route path="/cobros"            element={<CobrosPage />} />
                  <Route path="/cobros/trabajar"    element={<CobrosTrabajarRedirect />} />
                  <Route path="/cobros/trabajar/:id" element={<CobrosTrabajarRedirect />} />
                  <Route path="/impugnaciones"     element={<ImpugnacionesPage />} />
                  <Route path="/embargo"           element={<EmbargoPage />} />
                  <Route path="/habeas"            element={<HabeasPage />} />
                  <Route path="/clientes"          element={<ClientesPage />} />
                  <Route path="/legal/embargos"    element={<LegalGuard><EmbargosLegalPage /></LegalGuard>} />
                  <Route path="/legal/levantamientos" element={<LegalGuard><LevantamientosPage /></LegalGuard>} />
                  <Route path="/agenda"            element={<AgendaPage />} />
                  <Route path="/campanas"          element={<CampanasPage />} />
                  <Route path="/campanas/:id"      element={<MarcadoPage />} />
                  <Route path="/reportes"          element={<ReportesPage />} />
                  <Route path="/call-center"       element={<SupervisionPage />} />
                  <Route path="/call-center/marcador" element={<MarcadorListasPage />} />
                  <Route path="/pagos"             element={<PagosPage />} />
                  <Route path="/usuarios"          element={<UsuariosPage />} />
                  <Route path="/configuracion"     element={<ConfiguracionPage />} />
                  <Route path="/gestiones-legales" element={<GestionesLegalesPage />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
